import AppUser from "@toothfairy/shared-ui/AppUser";
import { useCallback, useEffect, useRef, useState } from "react";
import { Linking } from "react-native";
import API_Credits from "../API/API_Consumption/credits";
import {
  DEFAULT_AUTO_RECHARGE,
  DEFAULT_MONTHLY_CAP_USD,
  ENABLE_CONSUMPTION_BILLING,
  EXHAUSTED_CREDIT_THRESHOLD_USD,
  LOW_CREDIT_THRESHOLD_USD,
  RECHARGE_STATES,
  USD_PER_UOI,
} from "../Constants/billing";
import { getWebAppUrl } from "../Utils/regionWebUrl";

const RECHARGE_POLL_MS = 30000;
const isWeb = typeof window !== "undefined" && window.document;
const isElectron = isWeb && !!(window?.electronAPI);

// Recharge creates a Stripe Checkout (hosted) session server-side. The desktop
// (Electron) shell must NOT run hosted Checkout in-app (Apple Pay / 3DS / bank
// redirects / wallet returns are unreliable in a Chromium BrowserWindow and
// there is no deep-link back). Instead we hand off to the OS browser via the
// Electron preload bridge, exactly like the card/invoice/portal redirects.
const openCheckoutUrl = (url) => {
  if (isElectron && window?.electronAPI?.openUrlOnWeb) {
    window.electronAPI.openUrlOnWeb(url);
    return;
  }
  if (isWeb) {
    window.location.href = url;
  } else {
    Linking.openURL(url);
  }
};

// Determines the recharge state shown by the top-bar indicator.
export const computeRechargeState = (balanceUsd, lowThresholdUsd) => {
  if (balanceUsd <= EXHAUSTED_CREDIT_THRESHOLD_USD) return RECHARGE_STATES.EXHAUSTED;
  if (balanceUsd <= (lowThresholdUsd ?? LOW_CREDIT_THRESHOLD_USD))
    return RECHARGE_STATES.LOW;
  return RECHARGE_STATES.OK;
};

const useCredits = () => {
  const { user } = AppUser.useUser();
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recharging, setRecharging] = useState(false);
  const [saving, setSaving] = useState(false);
  const mountedRef = useRef(true);

  const refresh = useCallback(
    async (opts = {}) => {
      if (!ENABLE_CONSUMPTION_BILLING) return;
      if (!opts?.silent) setLoading(true);
      try {
        // Fetch the live Stripe customer object. The balance (credit) decreases
        // as usage is consumed, so it MUST be re-read each cycle — relying on the
        // auth-time user.stripeCustomerData leaves the UI showing a stale balance
        // (e.g. $5.00 after $0.03 of usage when Stripe already shows $4.97).
        // One /billing/customer call yields both the balance and the metadata
        // config (autoRecharge + spendCap), so we don't need a second hit.
        const { balanceUsd, customer } = await API_Credits.getCreditBalance();
        const config = customer
          ? {
              autoRecharge: API_Credits.parseRechargeConfig(customer).autoRecharge,
              spendCap: API_Credits.parseSpendCap(customer),
            }
          : null;
        if (mountedRef.current) {
          setCredits({
            balanceUsd,
            balanceUoI: Math.round(balanceUsd / USD_PER_UOI),
            autoRecharge: config?.autoRecharge,
            spendCap: config?.spendCap || { monthlyMaxUsd: DEFAULT_MONTHLY_CAP_USD },
          });
        }
      } catch (e) {
        // swallow — the UI falls back to the stripe customer balance if present
      } finally {
        if (mountedRef.current && !opts?.silent) setLoading(false);
      }
    },
    // eslint-disable-next-line react-components/exhaustive-deps
    [],
  );

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    const interval = setInterval(() => refresh({ silent: true }), RECHARGE_POLL_MS);
    // When the desktop (Electron) app regains focus after a recharge hand-off
    // to the OS browser, the in-app ?recharged=1 confirm path can't fire (the
    // params landed in the browser). Refresh on focus so the live balance — and
    // any workspace-creation gate — updates immediately instead of waiting up
    // to RECHARGE_POLL_MS.
    const handleFocus = () => {
      if (isElectron) refresh({ silent: true });
    };
    if (isWeb) window.addEventListener("focus", handleFocus);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      if (isWeb) window.removeEventListener("focus", handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const balanceUsd = (() => {
    if (credits?.balanceUsd != null) return credits.balanceUsd;
    return user?.stripeCustomerData?.balance ? user.stripeCustomerData.balance / -100 : 0;
  })();

  const balanceUoI = credits?.balanceUoI ?? Math.round(balanceUsd / USD_PER_UOI);
  const autoRecharge = credits?.autoRecharge ?? DEFAULT_AUTO_RECHARGE;
  const spendCap = credits?.spendCap ?? { monthlyMaxUsd: DEFAULT_MONTHLY_CAP_USD };
  const rechargeState = computeRechargeState(balanceUsd, autoRecharge?.thresholdUsd);

  // Recharge creates a Stripe Checkout (hosted) session server-side; the user
  // is sent to the hosted Checkout `url`. Credit is granted on
  // checkout.session.completed (backend), so the live balance updates after the
  // user returns + the next refresh poll (or a manual refresh on focus / route
  // change). In Electron we hand off to the OS browser via the preload bridge
  // (see openCheckoutUrl) and point returnTo at the regional web app, since the
  // in-app ?recharged=1&session_id=... confirm path can't fire from a browser.
  const recharge = useCallback(
    async ({ amountUsd, returnTo } = {}) => {
      if (!ENABLE_CONSUMPTION_BILLING) return null;
      setRecharging(true);
      try {
        const effectiveReturnTo =
          returnTo ?? (isElectron ? getWebAppUrl("/profile/billing") : undefined);
        const result = await API_Credits.rechargeCredits({
          amountUsd,
          returnTo: effectiveReturnTo,
        });
        // apisauce returns { ok, status, data, ... }; the body ({url, sessionId})
        // is in `data`. Read both shapes for safety.
        const url = result?.data?.url || result?.url;
        if (url) {
          openCheckoutUrl(url);
        }
        return result;
      } finally {
        if (mountedRef.current) setRecharging(false);
      }
    },
    [],
  );

  // Synchronously confirm a recharge after the user returns from Stripe
  // Checkout. The backend grants the credit idempotently if the webhook hasn't
  // fired yet and returns the updated balance. Used by the redirect handler so
  // the workspace-creation gate sees the real balance instead of a stale 0.
  const confirmingRef = useRef(false);
  const [confirmingRecharge, setConfirmingRecharge] = useState(false);
  const confirmRecharge = useCallback(
    async ({ sessionId } = {}) => {
      if (!ENABLE_CONSUMPTION_BILLING || !sessionId) return null;
      if (confirmingRef.current) return null;
      confirmingRef.current = true;
      setConfirmingRecharge(true);
      try {
        const result = await API_Credits.confirmRechargeSession({ sessionId });
        const balanceUsd = result?.data?.balanceUsd;
        if (typeof balanceUsd === "number") {
          setCredits((prev) => ({
            ...prev,
            balanceUsd,
            balanceUoI: Math.round(balanceUsd / USD_PER_UOI),
          }));
        }
        // Also re-fetch the authoritative customer object so
        // stripeCustomerData (and the progress bar / gates) update.
        try {
          await refresh({ silent: true });
        } catch (e) {
          // swallow — the setCredits above is the fallback
        }
        return result?.data;
      } finally {
        confirmingRef.current = false;
        if (mountedRef.current) setConfirmingRecharge(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const updateAutoRecharge = useCallback(
    async (autoRechargeConfig) => {
      if (!ENABLE_CONSUMPTION_BILLING) return null;
      setSaving(true);
      try {
        const result = await API_Credits.updateRechargeConfig({
          autoRecharge: autoRechargeConfig,
        });
        setCredits((prev) => (prev ? { ...prev, autoRecharge: autoRechargeConfig } : prev));
        return result;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  // Account-level monthly spend cap (USD). null = no cap. Persisted to the
  // Stripe customer metadata; enforced across all the user's workspaces by the
  // consumption watcher.
  const updateSpendCap = useCallback(
    async (monthlyMaxUsd) => {
      if (!ENABLE_CONSUMPTION_BILLING) return null;
      setSaving(true);
      try {
        const result = await API_Credits.updateSpendCap({
          monthlyMaxUsd: monthlyMaxUsd && monthlyMaxUsd > 0 ? Number(monthlyMaxUsd) : null,
        });
        setCredits((prev) =>
          prev
            ? { ...prev, spendCap: { monthlyMaxUsd: monthlyMaxUsd || null } }
            : prev,
        );
        return result;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  // Combined, atomic save of BOTH rechargeConfig and spendCap in a single POST.
  // The backend does a read-merge-write per request; two concurrent calls race
  // and clobber each other's metadata. Use this when persisting both at once.
  const updateBillingSettings = useCallback(
    async ({ spendCap: capUsd, autoRecharge: arConfig } = {}) => {
      if (!ENABLE_CONSUMPTION_BILLING) return null;
      setSaving(true);
      try {
        const spendCap =
          capUsd !== undefined
            ? { monthlyMaxUsd: capUsd && capUsd > 0 ? Number(capUsd) : null }
            : undefined;
        const rechargeConfig =
          arConfig !== undefined ? { autoRecharge: arConfig } : undefined;
        const result = await API_Credits.updateBillingSettings({
          spendCap,
          rechargeConfig,
        });
        setCredits((prev) => {
          const next = { ...prev };
          if (spendCap !== undefined) next.spendCap = spendCap;
          if (rechargeConfig !== undefined) next.autoRecharge = arConfig;
          return next;
        });
        return result;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  return {
    enabled: ENABLE_CONSUMPTION_BILLING,
    loading,
    recharging,
    saving,
    balanceUsd,
    balanceUoI,
    autoRecharge,
    spendCap,
    rechargeState,
    refresh,
    recharge,
    confirmRecharge,
    confirmingRecharge,
    updateAutoRecharge,
    updateSpendCap,
    updateBillingSettings,
  };
};

export default useCredits;